import { Button } from "@components/ui/button";
import { RefreshCw, Mail } from "lucide-react";

interface ErrorStateProps {
  title: string;
  description: string;
  retryButtonText?: string;
  onRetry?: () => void;
  showContactSupport?: boolean;
  contactSupportText?: string;
  contactSupportLabel?: string;
  illustration?: React.ReactNode;
}

export function ErrorState({
  title,
  description,
  retryButtonText = "Réessayer",
  onRetry,
  showContactSupport = true,
  contactSupportText = "Le problème persiste ?",
  contactSupportLabel = "Contactez-nous",
  illustration,
}: ErrorStateProps) {
  return (
    <div className="bg-background flex h-screen w-full items-center justify-center">
      <div className="flex max-w-md flex-col items-center gap-6 px-6 text-center">
        {/* Illustration personnalisable */}
        {illustration || (
          <div className="relative">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-50">
              <svg className="h-16 w-16 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            {/* Signal faible */}
            <div className="absolute -top-1 -right-1">
              <div className="flex gap-0.5">
                <div className="h-2 w-1 rounded-full bg-gray-300" />
                <div className="h-3 w-1 rounded-full bg-gray-300" />
                <div className="h-4 w-1 rounded-full bg-gray-400" />
              </div>
            </div>
          </div>
        )}

        {/* Message principal */}
        <div className="space-y-2">
          <h2 className="text-foreground text-2xl font-semibold">{title}</h2>
          <p className="text-muted-foreground">{description}</p>
        </div>

        {/* Bouton Réessayer */}
        {onRetry && (
          <Button onClick={onRetry} size="lg" className="w-full">
            <RefreshCw className="mr-2 h-4 w-4" />
            {retryButtonText}
          </Button>
        )}

        {/* Contact support */}
        {showContactSupport && (
          <div className="text-muted-foreground space-y-2 text-sm">
            <p>{contactSupportText}</p>
            <a
              href="mailto:support@doctinum.com"
              className="text-primary inline-flex items-center gap-1 hover:underline"
            >
              <Mail className="h-3 w-3" />
              {contactSupportLabel}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
